import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { PaymentMethod } from '../types/models/PaymentMethod';
import type { ApiResponsePaymentMethodList } from '../types/api/ApiResponsePaymentMethod';

export interface UsePaymentMethodsReturn {
  paymentMethods: PaymentMethod[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * `GET /clients/payment-methods` — the tenant's saved cards, re-synced from Mollie server-side on
 * every call (docs/api-types/payment-methods.md). Lists `valid` and `invalid` cards; pending setups,
 * failed setups and revoked cards are hidden by the backend.
 *
 * `enabled` defers the fetch until the surface that shows the cards is actually reached.
 */
export const usePaymentMethods = (enabled: boolean = true): UsePaymentMethodsReturn => {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [error, setError] = useState<string | null>(null);

  const fetchPaymentMethods = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/clients/payment-methods');
      const body = (await response.json().catch(() => null)) as ApiResponsePaymentMethodList | null;
      if (body?.status === 'success' && body.data?.payment_methods) {
        setPaymentMethods(body.data.payment_methods);
        return;
      }
      setError(body?.message ?? 'Could not load your saved cards.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while loading your saved cards.');
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  useEffect(() => {
    if (enabled && auth.isAuthenticated) {
      void fetchPaymentMethods();
    }
  }, [enabled, auth.isAuthenticated, fetchPaymentMethods]);

  return { paymentMethods, loading, error, refetch: fetchPaymentMethods };
};

export default usePaymentMethods;
